import React, { createContext, useContext, useState, useCallback } from 'react';
import { useURLParams } from '../hooks/admin/useURLParams';
import { usePagination } from '../hooks/admin/usePagination';
import apiService from '../services/api';

export interface Article {
  id: number;
  title: string;
  url: string;
  content?: string;
  publishedAt: string;
  scrapedAt: string;
  hasAnalysis: boolean;
  hasPendingJob: boolean;
  relevanceChecked: boolean;
  isRelevant: boolean;
  relevanceScore?: number;
  extractionFailed?: boolean;
  extractionError?: string;
  latestAnalysis?: {
    id: number;
    overallRiskScore: number;
    categoryScores: Record<string, number>; // Dynamic categories
    alertLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    processedAt: string;
    countryCode: string;
    threatActorId?: number;
    threatActorMentions?: string[];
    summary?: string;
    keyPoints?: string[]; // AI-generated key bullet points
    keyEntities?: string[]; // People, places, organizations
    confidenceLevel?: number; // AI certainty (0-1)
    reasoning?: Record<string, any>; // AI explanation/reasoning
  };
  source: {
    id: number;
    name: string;
    sourceType: string;
    requiresScrapingForRelevance: boolean;
    requiresScrapingForAnalysis: boolean;
  };
}

export interface ArticlesContextValue {
  // State
  articles: Article[];
  loading: boolean;
  error: string | null;
  selectedSourceId: number | null;
  viewingArticle: Article | null;
  
  // Pagination
  pagination: {
    currentPage: number;
    pageSize: number;
    totalPages: number;
    totalCount: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  
  // Actions
  setSelectedSourceId: (sourceId: number | null) => void;
  setViewingArticle: (article: Article | null) => void;
  fetchArticles: () => void;
  refreshArticles: () => void;
  triggerAnalysis: (articleId: number, useRelevanceCheck?: boolean) => Promise<void>;
  
  // Pagination actions
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  goToFirstPage: () => void;
  goToLastPage: () => void;
  goToNextPage: () => void;
  goToPrevPage: () => void;
}

const ArticlesContext = createContext<ArticlesContextValue | undefined>(undefined);

export function useArticles(): ArticlesContextValue {
  const context = useContext(ArticlesContext);
  if (!context) {
    throw new Error('useArticles must be used within an ArticlesProvider');
  }
  return context;
}

interface ArticlesProviderProps {
  children: React.ReactNode;
}

export function ArticlesProvider({ children }: ArticlesProviderProps) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewingArticle, setViewingArticle] = useState<Article | null>(null);
  
  // URL state for source filtering
  const { params: urlParams, updateParams } = useURLParams({
    initialValues: {},
    paramTypes: {
      source: 'number'
    }
  });
  
  // Pagination state
  const [paginationState, paginationActions] = usePagination({
    defaultPageSize: 50,
    pageSizeOptions: [25, 50, 100]
  });

  // Direct pagination info setter to avoid dependency loops
  const setPaginationInfo = useCallback((info: {
    totalPages: number;
    total: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  }) => {
    paginationActions.updatePagination(info);
  }, [paginationActions]);

  const selectedSourceId = urlParams.source as number | null;

  const setSelectedSourceId = useCallback((sourceId: number | null) => {
    updateParams({ 
      source: sourceId,
      page: 1 // Reset to first page when changing source
    });
  }, [updateParams]);

  const fetchArticles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const offset = (paginationState.currentPage - 1) * paginationState.pageSize;
      const response = await apiService.getRecentArticles(
        selectedSourceId || undefined, 
        paginationState.pageSize, 
        offset
      );
      
      setArticles(response.articles || []);
      // Update pagination info directly instead of through actions to avoid dependency loop
      setPaginationInfo({
        totalPages: response.pagination.totalPages,
        total: response.pagination.total,
        hasNextPage: response.pagination.hasNextPage,
        hasPrevPage: response.pagination.hasPrevPage
      });
    } catch (err) {
      console.error('Failed to fetch articles:', err);
      setError('Failed to load articles');
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [selectedSourceId, paginationState.currentPage, paginationState.pageSize, setPaginationInfo]);

  const refreshArticles = useCallback(() => {
    fetchArticles();
  }, [fetchArticles]);

  const triggerAnalysis = useCallback(async (articleId: number, useRelevanceCheck?: boolean) => {
    try {
      await apiService.triggerArticleAnalysis({
        articleId,
        countryCode: 'EST',
        tier: undefined,  // Always use dynamic tier determination for consistency
        useRelevanceCheck
      });
      
      // Refresh articles to show the analysis results
      refreshArticles();
    } catch (err: any) {
      console.error('Failed to trigger analysis:', err);
      // Re-throw the original error with more details
      if (err.response?.data?.message) {
        throw new Error(err.response.data.message);
      } else if (err.message) {
        throw err;
      } else {
        throw new Error('Failed to trigger analysis');
      }
    }
  }, [refreshArticles]);

  // Enhanced pagination actions that also update URL
  const setPage = useCallback((page: number) => {
    paginationActions.setPage(page);
  }, [paginationActions]);

  const setPageSize = useCallback((size: number) => {
    paginationActions.setPageSize(size);
  }, [paginationActions]);

  const contextValue: ArticlesContextValue = {
    // State
    articles,
    loading,
    error,
    selectedSourceId,
    viewingArticle,
    
    // Pagination
    pagination: {
      currentPage: paginationState.currentPage,
      pageSize: paginationState.pageSize,
      totalPages: paginationState.totalPages,
      totalCount: paginationState.totalCount,
      hasNextPage: paginationState.hasNextPage,
      hasPrevPage: paginationState.hasPrevPage
    },
    
    // Actions
    setSelectedSourceId,
    setViewingArticle,
    fetchArticles,
    refreshArticles,
    triggerAnalysis,
    
    // Pagination actions
    setPage,
    setPageSize,
    goToFirstPage: paginationActions.goToFirstPage,
    goToLastPage: paginationActions.goToLastPage,
    goToNextPage: paginationActions.goToNextPage,
    goToPrevPage: paginationActions.goToPrevPage
  };

  return (
    <ArticlesContext.Provider value={contextValue}>
      {children}
    </ArticlesContext.Provider>
  );
}